import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  UpdatePasswordModel,
  UpdatePasswordResponseModel,
} from '@core/auth/models/update-password.model';
import { SignInResponseModel } from '@core/auth/models/user-login.model';
import { environment } from '@environments/environment';
import { BaseResponse } from '@shared/interfaces/base-response.interface';
import { UserInformation } from '@shared/interfaces/user-information.interface';
import { map, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _ofvBaseApi = environment.backendApiUrl;
  private readonly _userInfoKey = environment.userInfoStorageKey;
  private readonly _clientId = environment.clientId;
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _router: Router,
  ) {}

  sigIn(email: string, password: string): Observable<unknown> {
    const body = {
      clientId: this._clientId,
      email,
      password,
    };

    return this._httpClient
      .post<
        BaseResponse<SignInResponseModel>
      >(`${this._ofvBaseApi}/v1/auth/authenticate`, body)
      .pipe(
        tap((response) => {
          if (response.data.token) {
            const authData = {
              ...response.data,
              userId: 'SbrHelp',
              hasHealthPolicies: false,
            };
            this.setUserInfo(authData);
          }
        }),
      );
  }

  refreshToken(): Observable<BaseResponse<SignInResponseModel>> {
    const token = this.getToken();
    const userInfo = this.getUserInfo();
    const refreshTokenValue = userInfo?.refreshToken;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    });
    const body = { refreshToken: refreshTokenValue };

    return this._httpClient
      .post<
        BaseResponse<SignInResponseModel>
      >(`${this._ofvBaseApi}/v1/auth/refresh`, body, { headers })
      .pipe(
        tap((response) => {
          if (response.data.token) {
            const authData = { ...response.data, userId: 'SbrHelp' };
            this.setUserInfo(authData);
          }
        }),
      );
  }

  recoverPassword(email: string, clientId: string): Observable<unknown> {
    return this._httpClient.post<unknown>(
      `${this._ofvBaseApi}/v2/users/Password`,
      {
        email,
        clientId,
      },
    );
  }

  public setUserInfo(signInResponse: SignInResponseModel): void {
    sessionStorage.setItem(this._userInfoKey, JSON.stringify(signInResponse));
  }

  getToken(): string | null {
    const userInfo = sessionStorage.getItem(this._userInfoKey);
    if (userInfo) {
      const stringUserInfo = JSON.parse(userInfo);
      return stringUserInfo.token;
    }
    return null;
  }

  getUserInfo(): SignInResponseModel | null {
    const userInfo = sessionStorage.getItem(this._userInfoKey);
    if (this.isAuthenticated()) {
      return JSON.parse(userInfo!);
    }
    return null;
  }

  updateTemporaryPasswordToFalse(): void {
    const userInfo = this.getUserInfo();
    if (userInfo) {
      userInfo.temporaryPassword = false;
      sessionStorage.removeItem(this._userInfoKey);
      this.setUserInfo(userInfo);
    }
  }

  getExpiryTimeSession(): number | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000;
  }

  isAuthenticated(): boolean {
    const expiryTime = this.getExpiryTimeSession();
    if (expiryTime == null) {
      return false;
    }
    if (Date.now() < expiryTime) {
      return true;
    }

    sessionStorage.clear();
    return false;
  }

  signOut(): void {
    sessionStorage.clear();
    this._router.navigate(['/iniciar-sesion']);
  }

  updateUserInfo(request: UserInformation): Observable<void> {
    const body: UserInformation = {
      externalId: request.externalId,
      idType: request.idType,
      idNumber: request.idNumber,
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.email,
      phoneNumber: request.phoneNumber,
      mobileNumber: request.mobileNumber,
      getUserInfo: request.getUserInfo,
    };

    return this._httpClient
      .post<BaseResponse<unknown>>(`${this._ofvBaseApi}/v2/users/update`, body)
      .pipe(
        map(() => {
          if (this.isAuthenticated()) {
            const userInfo = this.getUserInfo();
            userInfo!.phoneNumber = body.phoneNumber;
            userInfo!.mobileNumber = body.mobileNumber;
            this.setUserInfo(userInfo!);
          }
        }),
      );
  }

  updatePassword(
    changePasswordObject: UpdatePasswordModel,
  ): Observable<BaseResponse<UpdatePasswordResponseModel>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this._httpClient.put<BaseResponse<UpdatePasswordResponseModel>>(
      `${this._ofvBaseApi}/v2/users/Password`,
      changePasswordObject,
      {
        headers,
      },
    );
  }

  hasPermission(category: string): boolean {
    const userInfo = this.getUserInfo();

    if (userInfo === null) {
      return false;
    }

    if (category === 'Salud') {
      return userInfo?.hasHealthPolicies;
    }
    return true;
  }
}
